import type app from 'firebase/app';
import * as groupsApi from './groups';
import * as playersApi from './players';
import firebase from '../firebase/firebaseClient';

type Snapshot = app.database.DataSnapshot;
type Ref = app.database.Reference;
type ValueCallback = (snapshot: Snapshot | null) => void;
type ErrorCallback = (error: Error) => void;

type GameForm = {
  gameName: string;
  groupID: string;
  startTime: Date;
  maxTickets: number;
  maxTicketsPerPlayer: number;
  numberOfPrizes: number;
  ticketPrice: number;
  prohibitNegativeBalance?: boolean;
  randomness?: number;
  spinSpeed?: number;
  time?: number;
};

type GroupLike = {
  id: string;
  imageUrl?: string;
  groupName?: string;
};

type GameLike = {
  uid: string;
  key?: string;
  name: string;
  user: string;
  startTime: Date | null;
  groupCount: number;
  maxTickets: number;
  maxTicketsPerPlayer: number;
  numberOfPrizes: number;
  groupID: string;
  ticketPrice: number | string;
  prohibitNegativeBalance?: boolean;
  randomness?: number;
  spinSpeed?: number;
  tickets?: Record<string, number>;
  imageUrl?: string;
  groupName?: string;
};

type DbGameShape = {
  gameName: string;
  groupID: string;
  startTime: number;
  maxTickets: number;
  maxTicketsPerPlayer: number;
  numberOfPrizes: number;
  ticketPrice: number;
  prohibitNegativeBalance?: boolean;
  randomness?: number;
  spinSpeed?: number;
  time?: number;
  tickets?: Record<string, number>;
};

type DbTransactionShape = {
  amount: number | string;
  date: number;
  type: 'purchase' | 'payment';
};

const groupGamesRef = (groupId: string): Ref => firebase.db.ref(`groupGames/${groupId}`);

const groupGameRef = (groupId: string, gameId: string): Ref => firebase.db.ref(`groupGames/${groupId}/${gameId}`);

const gamesRef = (): Ref => firebase.db.ref('games');

const gameRef = (gameID: string): Ref => firebase.db.ref(`games/${gameID}`);

const ticketsRef = (gameId: string): Ref => firebase.db.ref(`games/${gameId}/tickets`);

const ticketRef = (gameId: string, playerId: string): Ref => firebase.db.ref(`games/${gameId}/tickets/${playerId}`);

const allGameRunnerRef = (): Ref => firebase.db.ref('gameRunner/');
const gameRunnerRef = (gameID: string): Ref => firebase.db.ref(`gameRunner/${gameID}`);

const gameStarterRef = (gameID: string): Ref => firebase.db.ref(`gameStarter/${gameID}`);

export const transactionGroupRef = (groupId: string): Ref => firebase.db.ref(`groupTransactions/${groupId}/`);

export const transactionRef = (groupId: string, uid: string, gameId: string): Ref => firebase.db.ref(`groupTransactions/${groupId}/${uid}/${gameId}`);

export const startGame = (gameId: string) => gameStarterRef(gameId).set({ [gameId]: gameId });

const addGameToGroup = (groupId: string, gameId: string) => {
  groupGamesRef(groupId)
    .once('value')
    .then(snapshot => ({
      ...snapshot.val(),
      [gameId]: true
    }))
    .then(updatedFields => groupGamesRef(groupId).update(updatedFields));
};

const mapToFirebaseGame = (gameForm: GameForm): DbGameShape => {
  const toOptionalNumber = (value: number | string | undefined) => {
    if (value === '' || value == null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const randomness = toOptionalNumber(gameForm.randomness);
  const spinSpeed = toOptionalNumber(gameForm.spinSpeed);

  return {
    gameName: gameForm.gameName,
    groupID: gameForm.groupID,
    startTime: gameForm.startTime.getTime(),
    maxTickets: gameForm.maxTickets,
    maxTicketsPerPlayer: gameForm.maxTicketsPerPlayer,
    numberOfPrizes: gameForm.numberOfPrizes,
    ticketPrice: gameForm.ticketPrice,
    ...(gameForm.prohibitNegativeBalance !== undefined
      ? { prohibitNegativeBalance: gameForm.prohibitNegativeBalance }
      : {}),
    ...(randomness !== undefined ? { randomness } : {}),
    ...(spinSpeed !== undefined ? { spinSpeed } : {}),
  };
};

export const add = (gameForm: GameForm) =>
  gamesRef().push(mapToFirebaseGame(gameForm))
    .then(ref => {
      const gameId = ref.key as string;
      const groupId = gameForm.groupID;
      addGameToGroup(groupId, gameId);
      return gameId;
    });

export const updateGame = (gameID: string, gameForm: GameForm) =>
  gameRef(gameID).update(mapToFirebaseGame(gameForm));

const mapToGame = (gameSnap: Snapshot, imageUrl?: string, groupName?: string): GameLike => {
  const dbGame = (gameSnap.val() || {}) as Partial<DbGameShape> & { user?: string; groupCount?: number };

  return {
    uid: gameSnap.key || '',
    name: dbGame.gameName || '',
    user: dbGame.user || '',
    startTime: dbGame.startTime ? new Date(dbGame.startTime) : null,
    groupCount: dbGame.groupCount || 0,
    maxTickets: dbGame.maxTickets || 0,
    maxTicketsPerPlayer: dbGame.maxTicketsPerPlayer || 0,
    numberOfPrizes: dbGame.numberOfPrizes || 0,
    groupID: dbGame.groupID || '',
    ticketPrice: dbGame.ticketPrice || '',
    prohibitNegativeBalance: dbGame.prohibitNegativeBalance,
    randomness: dbGame.randomness,
    spinSpeed: dbGame.spinSpeed,
    tickets: dbGame.tickets,
    imageUrl,
    groupName,
  };
};

export const fetchGame = (gameId: string) =>
  gameRef(gameId).once('value')
    .then(gameSnap => mapToGame(gameSnap));

export const fetchGamesForGroup = (group: GroupLike) => {
  const allPromises = groupGamesRef(group.id).once('value')
    .then(snapshot => {
      const promises: Array<Promise<GameLike>> = [];
      snapshot.forEach(childSnapshot => {
        const gameId = childSnapshot.key as string;
        const promise = gameRef(gameId).once('value')
          .then(gameSnap => mapToGame(gameSnap, group.imageUrl, group.groupName));
        promises.push(promise);
      });
      return Promise.all(promises);
    });

  return allPromises.then(games => games.map(game => ({
    id: game.key,
    ...game
  })));
};

export const fetchGames = (playerId: string) => {
  const allPromises = groupsApi.fetchPlayerGroups(playerId)
    .then((groups: GroupLike[]) => {
      const promises: Array<Promise<Array<GameLike>>> = [];
      groups.forEach((group: GroupLike) => {
        promises.push(fetchGamesForGroup(group));
      });
      return Promise.all(promises);
    });

  return allPromises.then((games: Array<Array<GameLike>>) => games.flat());
};

export const deleteGame = (groupId: string, gameId: string) => {
  gameRef(gameId)
    .remove();
  return groupGameRef(groupId, gameId).remove();
};

export const onGameChange = (gameId: string, successCallback: ValueCallback, errorCallback: ErrorCallback) => {
  gameRef(gameId).on('value', successCallback, errorCallback);
};

export const removeGameListener = (gameId: string) => gameRef(gameId).off();

export const onGameRunnerChange = (gameId: string, successCallback: ValueCallback, errorCallback: ErrorCallback) => {
  gameRunnerRef(gameId).on('value', successCallback, errorCallback);
};

export const startGameRunner = (gameId: string) =>
  firebase.functions
    .httpsCallable('startGame')({ gameId })
    .then(result => result.data);

export const removeGameRunnerListener = (gameId: string) => gameRunnerRef(gameId).off();

export const onTicketsChange = (gameId: string, successCallback: ValueCallback, errorCallback: ErrorCallback) => {
  ticketsRef(gameId).on('value', successCallback, errorCallback);
};

export const onTransactionsChange = (groupId: string, successCallback: ValueCallback, errorCallback: ErrorCallback) => {
  transactionGroupRef(groupId).on('value', successCallback, errorCallback);
};

export const removeTransactionsChangeListener = (gameId: string, successCallback?: ValueCallback) => {
  if (successCallback) {
    transactionGroupRef(gameId).off('value', successCallback);
    return;
  }
  transactionGroupRef(gameId).off();
};

export const removeTicketsListener = (gameId: string) => ticketsRef(gameId).off();

export const onPlayerTicketsChange = (gameId: string, playerId: string, successCallback: ValueCallback, errorCallback: ErrorCallback) => {
  ticketRef(gameId, playerId).on('value', successCallback, errorCallback);
};

export const removePlayerTicketsListener = (gameId: string, playerId: string) => ticketRef(gameId, playerId).off();

export const buyTicket = (gameId: string, playerId: string) => {
  const games = fetchGame(gameId);
  games.then(game => {
    const existingTickets = game.tickets ? (game.tickets[playerId] ?? 0) : 0;
    const transaction: DbTransactionShape = {
      amount: (Number(game.ticketPrice) * (existingTickets + 1)),
      date: new Date().getTime(),
      type: 'purchase'
    };
    transactionRef(game.groupID, playerId, gameId).set(transaction);
    ticketRef(gameId, playerId).set(existingTickets + 1);
  });
};

export const setFreeTickets = (gameId: string, playerId: string) => {
  const games = fetchGame(gameId);
  games.then(game => {
    const transaction: DbTransactionShape = {
      amount: 0,
      date: new Date().getTime(),
      type: 'purchase'
    };
    transactionRef(game.groupID, playerId, gameId).set(transaction);
  });
};

export const fetchAllGameRunners = () => allGameRunnerRef()
  .once('value')
  .then(snapshot => snapshot.val());

export const sellTicket = (gameId: string, playerId: string) => {
  const games = fetchGame(gameId);
  games.then(game => {
    const existingTickets = game.tickets ? (game.tickets[playerId] ?? 0) : 0;
    const newTicketCount = Math.max(existingTickets - 1, 0);
    if (newTicketCount === 0) {
      transactionRef(game.groupID, playerId, gameId)
        .remove();
      ticketRef(gameId, playerId).set(0);
    } else {
      const transaction: DbTransactionShape = {
        amount: newTicketCount * Number(game.ticketPrice),
        date: new Date().getTime(),
        type: 'purchase'
      };
      transactionRef(game.groupID, playerId, gameId)
        .set(transaction);
      ticketRef(gameId, playerId)
        .set(newTicketCount);
    }
  });
};

export const fillTestData = (gameId: string) => {
  const allPromises = playersApi.fetchPlayers()
    .then(players => {
      const promises: Array<Promise<void>> = [];
      for (let i = 0; i < 5; i += 1) {
        const rndIndex = Math.round(players.length * Math.random());
        const playerId = players[rndIndex].uid;
        const tickets = Math.round(1 + 2 * Math.random());
        promises.push(ticketRef(gameId, playerId).set(tickets));
      }
      return Promise.all(promises);
    });

  return allPromises;
};

// Remove gameRunner entry
export const resetGame = (gameId: string) => {
  return gameRunnerRef(gameId).remove();
};
